import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Funnel,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import Folder from '@/public/images/folder.svg'
import { cn } from '@/utils'

import {
  CUSTOM_EVENT_HIGHLIGHT_CLICK,
  CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
  EDocumentComparisonChangeType,
  EDocumentComparisonStatus,
  ETargetVersion,
} from '../../constants'
import { useAllVersionsReady } from '../../hooks'
import { IHTMLCompareChangeItem } from '../../html/types'
import { IPDFCompareChangeItem } from '../../pdf/types'
import { useVersionComparisonStore } from '../../store'
import { THighlightEventDetail } from '../../types'

/**
 * Display text for version comparison status
 */
const VERSION_COMPARISON_STATUS_DISPLAY_TEXT = {
  [EDocumentComparisonChangeType.INSERT]:
    'versionComparison.sidebar.status.inserted',
  [EDocumentComparisonChangeType.DELETE]:
    'versionComparison.sidebar.status.deleted',
  [EDocumentComparisonChangeType.REPLACE]:
    'versionComparison.sidebar.status.replaced',
}

/**
 * Sidebar changes list id for version comparison
 */
const SIDEBAR_CHANGES_LIST_ID = 'version-comparison-sidebar-changes-list'

type TComparisonSidebarProps = {
  isSidebarCollapsed: boolean
  onToggleCollapse: () => void
}

/**
 * Comparison Sidebar Component
 * Displays all changes between versions with filtering capabilities
 */
const ComparisonSidebar: React.FC<TComparisonSidebarProps> = (props) => {
  const { isSidebarCollapsed, onToggleCollapse } = props

  const { t } = useTranslation()
  const {
    filteredStatuses,
    compareDocumentStatus,
    compareDocumentDiff,
    focusedChangeUuid,
    setFilteredStatuses,
    setFocusedChangeUuid,
  } = useVersionComparisonStore()

  // Check if all versions are ready
  const allVersionsReady = useAllVersionsReady()

  /**
   * Scroll to focused change item when focusedChangeUuid changes
   * This effect runs when the focusedChangeUuid changes
   * and the sidebar is not collapsed
   */
  React.useEffect(() => {
    if (!focusedChangeUuid || isSidebarCollapsed) return

    const changesListContainer = document.getElementById(
      SIDEBAR_CHANGES_LIST_ID
    )
    if (!changesListContainer) return

    const element = changesListContainer.querySelector(
      `[data-uuid="${focusedChangeUuid}"]`
    )
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [focusedChangeUuid, isSidebarCollapsed])

  const changes = React.useMemo(() => {
    if (filteredStatuses.length === 0) return compareDocumentDiff
    return compareDocumentDiff?.filter((change) =>
      filteredStatuses.includes(change.type)
    )
  }, [compareDocumentDiff, filteredStatuses])

  const hasActiveFilters = React.useMemo(() => {
    return filteredStatuses.length > 0
  }, [filteredStatuses.length])

  const allStatuses = React.useMemo(() => {
    return Object.keys(
      VERSION_COMPARISON_STATUS_DISPLAY_TEXT
    ) as EDocumentComparisonChangeType[]
  }, [])

  const isLoading = React.useMemo(() => {
    return compareDocumentStatus === EDocumentComparisonStatus.COMPARING
  }, [compareDocumentStatus])

  const handleToggleStatus = React.useCallback(
    (status: EDocumentComparisonChangeType) => {
      const isSelected = filteredStatuses.includes(status)
      if (isSelected) {
        setFilteredStatuses(filteredStatuses.filter((s) => s !== status))
      } else {
        setFilteredStatuses([...filteredStatuses, status])
      }
    },
    [filteredStatuses, setFilteredStatuses]
  )

  const handleChangeItem = React.useCallback(
    (change: IHTMLCompareChangeItem | IPDFCompareChangeItem) => {
      if (!change.uuid) return

      setFocusedChangeUuid(change.uuid)
      const isInsert = change.type === EDocumentComparisonChangeType.INSERT

      // Determine target version based on change type
      let targetVersion: ETargetVersion = ETargetVersion.SOURCE
      if (isInsert) targetVersion = ETargetVersion.TARGET

      if (!targetVersion) return

      // Dispatch custom event to scroll to highlight
      const event = new CustomEvent<THighlightEventDetail>(
        CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
        {
          detail: { uuid: change.uuid, targetVersion, type: change.type },
        }
      )
      globalThis.dispatchEvent(event)
    },
    [setFocusedChangeUuid]
  )

  /**
   * Listen for highlight click events and update focused change UUID
   * This effect runs when the compareDocumentDiff changes
   * and the version is ready
   */
  React.useEffect(() => {
    // Check if comparison diff is available
    if (!compareDocumentDiff) return
    // Check if all versions are ready
    if (!allVersionsReady) return

    const handleHighlightClick = (event: Event) => {
      const customEvent = event as CustomEvent<THighlightEventDetail>
      const { uuid } = customEvent.detail || {}
      if (!uuid) return

      // Find the change item with the matching UUID
      const change = compareDocumentDiff.find((change) => change.uuid === uuid)
      if (!change) return

      handleChangeItem(change)
    }

    globalThis.addEventListener(
      CUSTOM_EVENT_HIGHLIGHT_CLICK,
      handleHighlightClick
    )

    return () => {
      globalThis.removeEventListener(
        CUSTOM_EVENT_HIGHLIGHT_CLICK,
        handleHighlightClick
      )
    }
  }, [allVersionsReady, compareDocumentDiff, handleChangeItem])

  /**
   * Handle first change item when focusedChangeUuid changes
   * This effect runs only one time
   * when the focusedChangeUuid is not set
   * and the compareDocumentDiff is available
   * and the all versions are ready
   */
  React.useEffect(() => {
    // Check if focused change UUID is set
    if (focusedChangeUuid) return
    // Check if compareDocumentDiff is available
    if (!compareDocumentDiff?.length) return
    // Check if all versions are ready
    if (!allVersionsReady) return

    const firstChangeWithUuid = compareDocumentDiff.find(
      (change) => change.uuid
    )
    if (firstChangeWithUuid) handleChangeItem(firstChangeWithUuid)
  }, [
    allVersionsReady,
    compareDocumentDiff,
    focusedChangeUuid,
    handleChangeItem,
  ])

  const handleNextChange = React.useCallback(() => {
    if (!changes || changes.length === 0) return

    // Find current focused change index
    const currentIndex = focusedChangeUuid
      ? changes.findIndex((change) => change.uuid === focusedChangeUuid)
      : -1

    // Calculate next index
    let nextIndex: number
    if (currentIndex === -1 || currentIndex === changes.length - 1) {
      // Not found or is last item, wrap to first
      nextIndex = 0
    } else {
      // Found and not last, move to next
      nextIndex = currentIndex + 1
    }

    const nextChange = changes[nextIndex]
    if (nextChange) handleChangeItem(nextChange)
  }, [changes, focusedChangeUuid, handleChangeItem])

  const handlePreviousChange = React.useCallback(() => {
    if (!changes || changes.length === 0) return

    // Find current focused change index
    const currentIndex = focusedChangeUuid
      ? changes.findIndex((change) => change.uuid === focusedChangeUuid)
      : -1

    // Calculate previous index
    let previousIndex: number
    if (currentIndex === -1 || currentIndex === 0) {
      // Not found or is first item, wrap to last
      previousIndex = changes.length - 1
    } else {
      // Found and not first, move to previous
      previousIndex = currentIndex - 1
    }

    const previousChange = changes[previousIndex]
    if (previousChange) handleChangeItem(previousChange)
  }, [changes, focusedChangeUuid, handleChangeItem])

  return (
    <div
      className={cn(
        'flex-shrink-0 pt-5',
        'transition-all duration-300',
        isSidebarCollapsed ? 'w-16' : 'w-sidebar'
      )}
    >
      <div className="w-sidebar h-full flex flex-col rounded-t-md rounded-bl-md border-l border-t border-secondary overflow-hidden">
        {/* Sidebar Header */}
        <div
          className={cn(
            'flex items-center justify-between flex-shrink-0 gap-3 border-b border-secondary bg-primary',
            isSidebarCollapsed ? 'px-5 py-xl' : 'p-xl'
          )}
        >
          {isSidebarCollapsed ? null : (
            <span className="text-primary-900 font-semibold text-lg">
              {t('versionComparison.sidebar.changes')}
            </span>
          )}

          <div className="flex flex-nowrap gap-md items-center">
            {/* Page navigation buttons */}
            {isSidebarCollapsed ? null : (
              <div className="flex gap-2">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={handleNextChange}
                      disabled={!changes || changes.length <= 1}
                      title={t('versionComparison.sidebar.nextChange')}
                      aria-label={t('versionComparison.sidebar.nextChange')}
                      leadingIcon={
                        <ChevronDown className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.sidebar.nextChange')}</span>
                  </TooltipContent>
                </Tooltip>

                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="text"
                      className="size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                      onClick={handlePreviousChange}
                      disabled={!changes || changes.length <= 1}
                      title={t('versionComparison.sidebar.previousChange')}
                      aria-label={t('versionComparison.sidebar.previousChange')}
                      leadingIcon={
                        <ChevronUp className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500 group-disabled:opacity-50" />
                      }
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.sidebar.previousChange')}</span>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            {isSidebarCollapsed ? null : (
              <div className="border-l h-4 border-secondary" />
            )}

            {/* Collapse/Expand Button */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="text"
                  className="flex-shrink-0 size-6 p-0 gap-0 flex items-center justify-center rounded-sm group"
                  onClick={onToggleCollapse}
                  title={
                    isSidebarCollapsed
                      ? t('versionComparison.sidebar.expandSidebar')
                      : t('versionComparison.sidebar.collapseSidebar')
                  }
                  aria-label={
                    isSidebarCollapsed
                      ? t('versionComparison.sidebar.expandSidebar')
                      : t('versionComparison.sidebar.collapseSidebar')
                  }
                  leadingIcon={
                    isSidebarCollapsed ? (
                      <PanelLeftOpen className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                    ) : (
                      <PanelLeftClose className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                    )
                  }
                />
              </TooltipTrigger>
              <TooltipContent>
                <span>
                  {isSidebarCollapsed
                    ? t('versionComparison.sidebar.expand')
                    : t('versionComparison.sidebar.collapse')}
                </span>
              </TooltipContent>
            </Tooltip>

            {isSidebarCollapsed ? null : (
              <div className="border-l h-4 border-secondary" />
            )}

            {/* Filter Popover */}
            {isSidebarCollapsed ? null : (
              <Popover>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant="text"
                        className="flex-shrink-0 size-6 p-0 gap-0 flex items-center justify-center relative rounded-sm group"
                        title={t('versionComparison.sidebar.filterStatuses')}
                        aria-label={t(
                          'versionComparison.sidebar.filterStatuses'
                        )}
                        leadingIcon={
                          <Funnel className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                        }
                      >
                        {hasActiveFilters && (
                          <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-foreground-fg-error-primary" />
                        )}
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>{t('versionComparison.sidebar.filterStatuses')}</span>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent
                  className="sm:max-w-[160px] p-0"
                  side="bottom"
                  align="end"
                >
                  <div className="py-1 px-1.5 space-y-0.5">
                    {allStatuses.map((status) => {
                      const statusKey =
                        status as keyof typeof VERSION_COMPARISON_STATUS_DISPLAY_TEXT
                      if (!VERSION_COMPARISON_STATUS_DISPLAY_TEXT[statusKey])
                        return null

                      const isSelected = filteredStatuses.includes(status)

                      return (
                        <button
                          key={status}
                          className={cn(
                            'p-sm font-medium text-sm cursor-pointer rounded-sm bg-transparent border-0 w-full hover:bg-primary-hover flex gap-2 items-center justify-between',
                            isSelected && 'bg-primary-hover'
                          )}
                          onClick={() => handleToggleStatus(status)}
                          aria-pressed={isSelected}
                        >
                          <span>
                            {t(
                              VERSION_COMPARISON_STATUS_DISPLAY_TEXT[statusKey]
                            )}
                          </span>
                          {isSelected && (
                            <Check
                              size={16}
                              className="text-foreground-fg-brand-primary-600"
                            />
                          )}
                        </button>
                      )
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Changes List Content */}
        {isSidebarCollapsed ? null : (
          <div
            id={SIDEBAR_CHANGES_LIST_ID}
            className="flex-1 bg-secondary-alt overflow-y-auto overflow-x-hidden"
          >
            {(() => {
              if (isLoading) {
                return (
                  <div className="flex items-center justify-center p-8 h-full">
                    <Spinner className="size-8 text-brand-600" />
                  </div>
                )
              }

              if (changes?.length === 0) {
                return (
                  <div className="p-4 h-full flex flex-col items-center justify-center">
                    <img
                      src={Folder}
                      alt="No data"
                      className="h-12 mb-4 w-14"
                    />
                    <h3 className="p-0 m-0 font-semibold text-primary-900 text-md">
                      {t('versionComparison.sidebar.noChangesDetected')}
                    </h3>
                  </div>
                )
              }

              return (
                <div className="p-2 space-y-2">
                  {changes?.map((change, index: number) => {
                    const changeNumber = index + 1
                    const changeTypeText =
                      t(VERSION_COMPARISON_STATUS_DISPLAY_TEXT[change.type]) ??
                      ''

                    const isReplace =
                      change.type === EDocumentComparisonChangeType.REPLACE
                    const isDelete =
                      change.type === EDocumentComparisonChangeType.DELETE
                    const isInsert =
                      change.type === EDocumentComparisonChangeType.INSERT
                    const contentDelete = (change.oldValue as string) ?? ''
                    const contentInsert = (change.newValue as string) ?? ''

                    // Check if content is valid for replace, delete, and insert
                    const isContentReplaceValid =
                      isReplace &&
                      typeof change.oldValue !== 'string' &&
                      typeof change.newValue !== 'string'
                    const isContentDeleteValid =
                      isDelete && typeof change.oldValue !== 'string'
                    const isContentInsertValid =
                      isInsert && typeof change.newValue !== 'string'

                    // Check if content is invalid
                    const isContentInvalid =
                      isContentReplaceValid ||
                      isContentDeleteValid ||
                      isContentInsertValid
                    if (isContentInvalid) return null

                    const deleteLength =
                      (change.oldValue as string)?.length ?? 0
                    const insertLength =
                      (change.newValue as string)?.length ?? 0

                    return (
                      <button
                        type="button"
                        key={change.uuid ?? index}
                        data-uuid={change.uuid ?? undefined}
                        className="flex gap-1 rounded-md w-full p-lg bg-primary border border-solid border-secondary text-xs cursor-pointer overflow-hidden"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleChangeItem(change)
                        }}
                      >
                        <div className="flex-shrink-0 font-semibold text-tertiary-600">
                          {`${changeNumber}.`}
                        </div>
                        <div className="flex-1 flex flex-col gap-1">
                          {/* First line: Change number + type on the left, number of changed on the right */}
                          <div className="flex gap-1 items-center">
                            <span className="font-semibold text-tertiary-600 uppercase mr-auto">
                              {changeTypeText}
                            </span>
                            {focusedChangeUuid === change.uuid && (
                              <div className="inline-block rounded-full size-3 min-w-3 bg-foreground-fg-brand-primary-alt" />
                            )}
                            {deleteLength ? (
                              <span className="text-error-primary-600">
                                {`-${deleteLength}`}
                              </span>
                            ) : null}
                            {isReplace ? (
                              <div className="inline-block border-r border-solid border-secondary h-4 mx-0.5" />
                            ) : null}
                            {insertLength ? (
                              <span className="text-brand-tertiary-600">
                                {`+${insertLength}`}
                              </span>
                            ) : null}
                          </div>

                          {/* Second line: Change content */}
                          <div className="flex flex-col gap-1 text-sm text-primary-900 break-words text-left">
                            {contentDelete ? (
                              <span className="px-0.5 w-fit bg-brand-utility-error-100">
                                {contentDelete}
                              </span>
                            ) : null}
                            {contentInsert ? (
                              <span className="px-0.5 w-fit bg-brand-utility-brand-100_alt">
                                {contentInsert}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}

const ComparisonSidebarMemoized = React.memo(ComparisonSidebar)
ComparisonSidebarMemoized.displayName = 'ComparisonSidebar'

export { ComparisonSidebarMemoized as ComparisonSidebar }
